import { LoadingContainer, Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import { Breadcrumb, Heading, Page } from 'components';
import { ReactElement } from 'react';
import type { EmbeddedUser } from 'modules/core/types';
import { Card } from '@ff-it/ui';
import useSWR from 'swr';
import { BlockStateTag } from 'modules/campaign/status/BlockStateTag';

interface Common {
  planning: number;
  submitted: number;
  pending: number;
  live: number;
  ended: number;
  total: number;
}

interface Manager extends Common {
  manager: EmbeddedUser;
  new: number;

  missing_income: number;
  missing_expense: number;
}

interface Planner extends Common {
  planner: EmbeddedUser;
}

export interface SummaryProps {
  managers: Manager[];
  planners: Planner[];
}

function CommonCols({ planning, submitted, pending, live, ended, total }: Common): ReactElement {
  return (
    <>
      <Td textAlign="center">{planning}</Td>
      <Td textAlign="center">{submitted}</Td>
      <Td textAlign="center">{pending}</Td>
      <Td textAlign="center">{live}</Td>
      <Td textAlign="center">{ended}</Td>
      <Td textAlign="center" fontWeight="bold">
        {total}
      </Td>
    </>
  );
}

function CommonHeader(): ReactElement {
  return (
    <>
      <Th textAlign="center">
        <BlockStateTag state="PLANNING" appearance="naked" />
      </Th>
      <Th textAlign="center">
        <BlockStateTag state="SUBMITTED" appearance="naked" />
      </Th>
      <Th textAlign="center">
        <BlockStateTag state="APPROVED" appearance="naked" iconOnly>
          PENDING
        </BlockStateTag>
      </Th>
      <Th textAlign="center">
        <BlockStateTag state="APPROVED" appearance="naked" iconOnly>
          LIVE
        </BlockStateTag>
      </Th>
      <Th textAlign="center">
        <BlockStateTag state="APPROVED" appearance="naked" iconOnly>
          ENDED
        </BlockStateTag>
      </Th>
      <Th textAlign="center">Total</Th>
    </>
  );
}

function RenderSummary({ managers, planners }: SummaryProps): ReactElement {
  return (
    <>
      <Card.Container mb="3">
        <Table size="sm" fixed>
          <Thead>
            <Tr>
              <Th style={{ width: '180px' }} />
              <Th colSpan={7} textAlign="center">
                Campaign
              </Th>
              <Th
                colSpan={3}
                textAlign="center"
                style={{
                  borderLeft: '1px solid #EAECF0FF',
                }}
              >
                Invoices
              </Th>
            </Tr>
            <Tr>
              <Th>Managed</Th>
              <Th textAlign="center">
                <BlockStateTag state="NEW" appearance="naked" />
              </Th>
              <CommonHeader />
              <Th
                textAlign="center"
                style={{
                  borderLeft: '1px solid #EAECF0FF',
                }}
              >
                Income
              </Th>
              <Th textAlign="center">Expense</Th>
              <Th textAlign="center">Total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {managers.map(
              ({ manager: { first_name, last_name }, new: newCnt, missing_income, missing_expense, ...common }, i) => (
                <Tr key={i}>
                  <Td>
                    {first_name} {last_name}
                  </Td>
                  <Td textAlign="center">{newCnt}</Td>
                  <CommonCols {...common} />
                  <Td
                    textAlign="center"
                    style={{
                      borderLeft: '1px solid #EAECF0FF',
                    }}
                  >
                    {missing_income}
                  </Td>
                  <Td textAlign="center">{missing_expense}</Td>
                  <Td textAlign="center" fontWeight="bold">
                    {missing_income + missing_expense}
                  </Td>
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      </Card.Container>
      <Card.Container mb="3">
        <Table size="sm" fixed>
          <Thead>
            <Tr>
              <Th style={{ width: '180px' }}>Planned</Th>
              <CommonHeader />
            </Tr>
          </Thead>
          <Tbody>
            {planners.map(({ planner: { first_name, last_name }, ...common }, i) => (
              <Tr key={i}>
                <Td>
                  {first_name} {last_name}
                </Td>
                <CommonCols {...common} />
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card.Container>
    </>
  );
}

export function Summary(): ReactElement {
  const { isLoading, data } = useSWR<SummaryProps>({
    method: 'GET',
    url: 'dashboard/summary/',
  });

  return (
    <Page narrow>
      <Breadcrumb>Summary</Breadcrumb>
      <Heading title="Summary" />
      <LoadingContainer loading={isLoading}>{data && <RenderSummary {...data} />}</LoadingContainer>
    </Page>
  );
}
